/**
 * DataView Component
 * Container that handles card grid or list table layout
 *
 * Sortable headers (opt-in, 29 Sep 2026): give a column a `sortKey` and pass `sort`
 * ({ key, dir }) and `onSort(key)`. The header becomes a button with an up/down indicator and
 * `aria-sort`; the page owns the order (utils/tableSort.js has the rules). Columns without a
 * `sortKey`, and tables without `onSort`, render exactly as before.
 */
import React from "react";
import PropTypes from "prop-types";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import "./DataView.scss";

const ARIA_SORT = { asc: "ascending", desc: "descending" };

export const DataView = ({ viewMode, children, columns, className = "", sort = null, onSort }) => {
  // Calculate grid template for headers (matching the list items)
  // Don't add " auto" here since the last column already includes the actions width
  const gridTemplate = columns
    ? columns.map((col) => col.width || "1fr").join(" ")
    : undefined;

  return (
    <div className={`data-view data-view--${viewMode} ${className}`}>
      {viewMode === "list" && columns && (
        <div
          className="data-view__header"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          {columns.map((column, index) => {
            const sortable = Boolean(column.sortKey && onSort);
            const active = sortable && sort && sort.key === column.sortKey;
            const Icon = active ? (sort.dir === "asc" ? FaSortUp : FaSortDown) : FaSort;
            return (
              <div
                key={index}
                role="columnheader"
                aria-sort={sortable ? (active ? ARIA_SORT[sort.dir] : "none") : undefined}
                className="data-view__header-cell"
                style={{
                  textAlign: column.align || "left",
                  justifyContent:
                    column.align === "right"
                      ? "flex-end"
                      : column.align === "center"
                      ? "center"
                      : "flex-start",
                }}
              >
                {sortable ? (
                  <button
                    type="button"
                    className={`data-view__sort${active ? " data-view__sort--active" : ""}`}
                    onClick={() => onSort(column.sortKey)}
                    aria-label={`Sort by ${column.label}`}
                    title={`Sort by ${column.label}`}
                  >
                    {column.label}
                    <Icon className="data-view__sort-icon" aria-hidden="true" />
                  </button>
                ) : (
                  column.label
                )}
              </div>
            );
          })}
        </div>
      )}
      {children}
    </div>
  );
};

DataView.propTypes = {
  viewMode: PropTypes.oneOf(["cards", "list"]).isRequired,
  children: PropTypes.node.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      width: PropTypes.string,
      align: PropTypes.oneOf(["left", "center", "right"]),
      sortKey: PropTypes.string,
    })
  ),
  className: PropTypes.string,
  sort: PropTypes.shape({
    key: PropTypes.string.isRequired,
    dir: PropTypes.oneOf(["asc", "desc"]).isRequired,
  }),
  onSort: PropTypes.func,
};
